import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { ArrowBack, ArrowForward, LocationOn, School, CheckCircle } from '@mui/icons-material';
import { useGuest } from '../context/GuestContext';
import locationService, { Country, Council, School as SchoolType } from '../services/locationService';
import { getGuestCoursesBySchoolId, registerGuestCourse } from '../services/guestApi';
import type { CourseDTO } from '../services/guestApi';
import { trackGuestEvent } from '../services/guestAnalyticsService';

const steps = ['Select Country', 'Select Council', 'Select School', 'Select Course'];

const GuestByLocationPage: React.FC = () => {
  const navigate = useNavigate();
  const { guestId, ensureSession } = useGuest();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [countries, setCountries] = useState<Country[]>([]);
  const [councils, setCouncilList] = useState<Council[]>([]);
  const [schools, setSchools] = useState<SchoolType[]>([]);
  const [courses, setCourses] = useState<CourseDTO[]>([]);

  const [selectedCountry, setSelectedCountry] = useState<number | ''>('');
  const [selectedCouncil, setSelectedCouncil] = useState<number | ''>('');
  const [selectedSchool, setSelectedSchool] = useState<number | ''>('');
  const [selectedCourse, setSelectedCourse] = useState<number | ''>('');
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [gid, data] = await Promise.all([ensureSession(), locationService.getCountries()]);
        if (!cancelled) {
          setCountries(data);
          trackGuestEvent(gid, { eventType: 'path_selected', entryMethod: 'BY_LOCATION' });
        }
      } catch {
        if (!cancelled) setError('Could not load countries.');
      }
    })();
    return () => { cancelled = true; };
  }, [ensureSession]);

  useEffect(() => {
    if (!selectedCountry) {
      setCouncilList([]);
      setSelectedCouncil('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await locationService.getCouncils(selectedCountry as number);
        if (!cancelled) setCouncilList(data);
      } catch (e) {
        if (!cancelled) setError('Failed to load councils.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCountry]);

  useEffect(() => {
    if (!selectedCouncil) {
      setSchools([]);
      setSelectedSchool('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await locationService.getSchoolsByCouncil(selectedCouncil as number);
        if (!cancelled) setSchools(data);
      } catch (e) {
        if (!cancelled) setError('Failed to load schools.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCouncil]);

  useEffect(() => {
    if (!selectedSchool) {
      setCourses([]);
      setSelectedCourse('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getGuestCoursesBySchoolId(selectedSchool as number);
        if (!cancelled) {
          setCourses(data);
          const gid = guestId ?? localStorage.getItem('guestId');
          trackGuestEvent(gid, {
            eventType: 'location_courses_viewed',
            entryMethod: 'BY_LOCATION',
            countryId: selectedCountry as number,
            councilId: selectedCouncil as number,
            schoolId: selectedSchool as number,
            metadata: { course_count: data.length },
          });
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load courses for this school.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSchool, selectedCountry, selectedCouncil, guestId]);

  const handleNext = () => {
    setError('');
    if (activeStep === 0 && !selectedCountry) {
      setError('Please select a country');
      return;
    }
    if (activeStep === 1 && !selectedCouncil) {
      setError('Please select a council');
      return;
    }
    if (activeStep === 2 && !selectedSchool) {
      setError('Please select a school');
      return;
    }
    if (activeStep === 3) {
      handleSelectCourseAndContinue();
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleSelectCourseAndContinue = async () => {
    const gid = guestId ?? localStorage.getItem('guestId');
    if (!gid) {
      setError('Guest session not ready. Please refresh.');
      return;
    }
    if (!selectedCourse) {
      setError('Please select a course');
      return;
    }
    setError('');
    setRegistering(true);
    try {
      const res = await registerGuestCourse(gid, {
        courseId: selectedCourse as number,
        entryMethod: 'BY_LOCATION',
        countryId: selectedCountry as number,
        councilId: selectedCouncil as number,
        schoolId: selectedSchool as number,
      });
      navigate(`/guest/course/${res.courseId}`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message ?? (e instanceof Error ? e.message : 'Registration failed'));
    } finally {
      setRegistering(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select your country
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Country</InputLabel>
              <Select
                value={selectedCountry}
                onChange={(e) => {
                  const id = e.target.value as number;
                  setSelectedCountry(id);
                  const gid = guestId ?? localStorage.getItem('guestId');
                  trackGuestEvent(gid, {
                    eventType: 'location_country',
                    entryMethod: 'BY_LOCATION',
                    countryId: id,
                  });
                }}
                label="Country"
                disabled={loading || countries.length === 0}
              >
                {countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {countries.length === 0 && !loading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No countries found.
              </Alert>
            )}
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select your council
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Council</InputLabel>
              <Select
                value={selectedCouncil}
                onChange={(e) => {
                  const id = e.target.value as number;
                  setSelectedCouncil(id);
                  const gid = guestId ?? localStorage.getItem('guestId');
                  trackGuestEvent(gid, {
                    eventType: 'location_council',
                    entryMethod: 'BY_LOCATION',
                    countryId: selectedCountry as number,
                    councilId: id,
                  });
                }}
                label="Council"
                disabled={!selectedCountry || loading || councils.length === 0}
              >
                {councils.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {councils.length === 0 && selectedCountry && !loading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No councils found for the selected country.
              </Alert>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select your school
            </Typography>
            <FormControl fullWidth>
              <InputLabel>School</InputLabel>
              <Select
                value={selectedSchool}
                onChange={(e) => {
                  const id = e.target.value as number;
                  setSelectedSchool(id);
                  const gid = guestId ?? localStorage.getItem('guestId');
                  trackGuestEvent(gid, {
                    eventType: 'location_school',
                    entryMethod: 'BY_LOCATION',
                    countryId: selectedCountry as number,
                    councilId: selectedCouncil as number,
                    schoolId: id,
                  });
                }}
                label="School"
                disabled={!selectedCouncil || loading || schools.length === 0}
              >
                {schools.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {schools.length === 0 && selectedCouncil && !loading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No schools found for the selected council.
              </Alert>
            )}
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select a course to try
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : courses.length === 0 ? (
              <Alert severity="info">
                No courses available for this school. Try another school or use “Search and try a topic” from the guest page.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {courses.map((c) => (
                  <Grid item xs={12} sm={6} key={c.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: selectedCourse === c.id ? 'primary.main' : 'grey.300',
                      }}
                      onClick={() => setSelectedCourse(c.id)}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {c.name}
                        </Typography>
                        {c.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {c.description.slice(0, 100)}
                            {c.description.length > 100 ? '…' : ''}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/guest/available')} sx={{ mb: 2 }}>
        Back
      </Button>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
            Browse courses by my school
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select your country, council and school to see courses. You can try 2 topics, then sign up to enrol.
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {renderStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0 || loading || registering}
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
          {activeStep < 3 ? (
            <Button variant="contained" onClick={handleNext} disabled={loading}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading || registering || !selectedCourse || courses.length === 0}
              endIcon={registering ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {registering ? 'Registering…' : 'Try this course'}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default GuestByLocationPage;
